<template>
  <v-form>
    <px-view-header title="Solicitar confirmação"
                    title-class="font-weight-bold"
                    :breadcrumbs="breadcrumbs">
      <template v-slot:before>
        <v-btn class="mb-4 mr-1" icon x-large color="primary" link @click="onClickBack">
          <v-icon>
            mdi-arrow-left
          </v-icon>
        </v-btn>
      </template>
    </px-view-header>
    <div v-if="loading">
      <px-circular-loading/>
    </div>
    <div class="mt-3" v-if="!loading">
      <!--          Dados gerais-->
      <v-row>
        <v-col cols="12">
          <span class="headline font-weight-bold title">
            Detalhamento do pedido
            <px-moeda-chip v-if="isMoedaEstrangeira"
                           :moeda="moeda"/>
          </span>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-subheader class="body-1 grey lighten-4 text--primary">
            <v-icon>
              mdi-truck-outline
            </v-icon>
            <span class="ml-2">Frete</span>
          </v-subheader>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" sm="5">
          <px-autocomplete-transportadora
            dense
            :class="`rounded-tag-small ${isTransportadoraRequired ? 'required' : ''}`"
            search-by-code
            show-codigo
            path="fornecedor/score"
            :error="$v.finalizacao.transportadora.$error"
            @blur="$v.finalizacao.transportadora.$touch()"
            v-model="finalizacao.transportadora"/>
        </v-col>
        <v-col cols="12" sm="2">
          <px-select-enum dense
                          label="Tipo de frete"
                          class="required rounded-tag-small"
                          :error="$v.finalizacao.frete.tipoFrete.$error"
                          @blur="$v.finalizacao.frete.tipoFrete.$touch()"
                          v-model="finalizacao.frete.tipoFrete"
                          @change="onChangeTipoFrete"
                          :items="tiposFretes"/>
        </v-col>
        <v-col cols="12" sm="2">
          <px-input-currency
            dense
            label="Valor do Frete"
            class="rounded-tag-small"
            :options="configuracaoMoeda"
            v-model="finalizacao.frete.totalTransporte"/>
        </v-col>
        <v-col cols="12" sm="3">
          <px-date-picker dense
                          label="Previsão de entrega"
                          input-class="required rounded-tag-small"
                          :error="$v.finalizacao.dataPrevisaoEntrega.$error"
                          @blur="$v.finalizacao.dataPrevisaoEntrega.$touch()"
                          v-model="finalizacao.dataPrevisaoEntrega"/>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <px-autocomplete-comprador
            dense
            required
            label="Local de entrega"
            class="required rounded-tag-small"
            :error="$v.finalizacao.comprador.$error"
            @blur="$v.finalizacao.comprador.$touch()"
            v-model="finalizacao.compradorEntrega"/>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-subheader class="body-1 grey lighten-4 text--primary">
            <v-icon>
              mdi-currency-usd
            </v-icon>
            <span class="ml-2">Faturamento</span>
          </v-subheader>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" sm="3">
          <px-select-enum
            dense
            label="Forma de pagamento"
            class="required rounded-tag-small"
            v-model="finalizacao.formaPagamento"
            :error="$v.finalizacao.formaPagamento.$error"
            @blur="$v.finalizacao.formaPagamento.$touch()"
            :items="formasPagamentos"
            @change="onChangeFormaPagamento"
          />
        </v-col>
        <v-col cols="12" sm="3">
          <px-autocomplete-prazo-pagamento
            dense
            label="Prazo de pagamento"
            :class="isAVista ? 'rounded-tag-small' : 'required rounded-tag-small'"
            :disabled="isAVista"
            :error="$v.finalizacao.prazoPagamento.$error"
            @blur="$v.finalizacao.prazoPagamento.$touch()"
            v-model="finalizacao.prazoPagamento"/>
        </v-col>
        <v-col cols="12" :sm="smModoPagamento">
          <px-autocomplete-modo-de-pagamento
            dense
            :class="`rounded-tag-small ${isModoPagamentoRequired ? 'required' : ''}`"
            :error="$v.finalizacao.modoPagamento.$error"
            @blur="$v.finalizacao.modoPagamento.$touch()"
            v-model="finalizacao.modoPagamento"/>
        </v-col>
        <v-col cols="12" sm="3" v-if="hasDataNecessidadeEntregaSolicitacao">
          <px-date-picker dense
                          label="Data de entrega solicitante"
                          input-class="rounded-tag-small"
                          v-model="finalizacao.dataNecessidadeEntregaSolicitacao"/>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" sm="5">
          <px-autocomplete-comprador
            dense
            required
            label="Local de faturamento"
            class="required rounded-tag-small"
            :error="$v.finalizacao.comprador.$error"
            @blur="$v.finalizacao.comprador.$touch()"
            v-model="finalizacao.comprador"/>
        </v-col>
        <v-col cols="12" sm="4">
          <px-autocomplete-nota-entrada
            dense
            :class="`rounded-tag-small ${isTipoNotaEntradaRequired ? 'required' : ''}`"
            :error="$v.finalizacao.tipoNotaEntrada.$error"
            @blur="$v.finalizacao.tipoNotaEntrada.$touch()"
            v-model="finalizacao.tipoNotaEntrada"/>
        </v-col>
        <v-col cols="12" sm="3">
          <px-date-picker dense
                          label="Data de Faturamento"
                          input-class="rounded-tag-small"
                          :error="$v.finalizacao.dataFaturamento.$error"
                          @blur="$v.finalizacao.dataFaturamento.$touch()"
                          v-model="finalizacao.dataFaturamento"/>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-subheader class="body-1 grey lighten-4 text--primary">
            <v-icon>
              mdi-text-box-outline
            </v-icon>
            <span class="ml-2">Dados gerais</span>
          </v-subheader>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" sm="6">
          <px-autocomplete-centro-custo
            dense
            :class="`rounded-tag-small ${isCentroCustoRequired ? 'required' : ''}`"
            :error="$v.finalizacao.centroCusto.$error"
            @blur="$v.finalizacao.centroCusto.$touch()"
            v-model="finalizacao.centroCusto"/>
        </v-col>
        <v-col cols="12" sm="6">
          <px-autocomplete-projeto
            dense
            class="rounded-tag-small"
            v-model="finalizacao.projeto"/>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-textarea
            outlined
            auto-grow
            class="rounded-tag-small"
            v-model="finalizacao.observacao"
            label="Observações internas"
            counter="1000"
            rows="2"/>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-textarea
            outlined
            auto-grow
            class="rounded-tag-small"
            v-model="finalizacao.mensagem"
            label="Mensagem para o fornecedor"
            counter="1000"
            rows="2">
            <template v-slot:append-outer>
              <px-anexo v-if="!isAprovacaoEnabled"
                        v-model="arquivos"
                        :max="3"
                        @loading="onFileLoad">
                <template v-slot:activator="{ on }">
                  <v-btn x-small class="normal-btn-text elevation-0"
                         color="primary" v-on="on">
                    Ordem de compra
                  </v-btn>
                </template>
              </px-anexo>
            </template>
          </v-textarea>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-subheader class="body-1 grey lighten-4 text--primary">
            <v-icon>
              mdi-comment-processing-outline
            </v-icon>
            <span class="ml-2">Mensagens personalizadas</span>
          </v-subheader>
        </v-col>
      </v-row>
      <px-finalizacao-cadastro-mensagens-personalizadas
        :finalizacao="finalizacao"
        @remove="onRemoveMessage"/>
      <v-row dense v-if="erpCamposIntegracao">
        <v-col cols="12">
          <v-subheader class="body-1 grey lighten-4 text--primary">
            <v-icon>
              mdi-progress-wrench
            </v-icon>
            <span class="ml-2">Campos de integração</span>
          </v-subheader>
        </v-col>
      </v-row>
      <v-row dense v-if="erpCamposIntegracao">
        <v-col cols="12">
          <px-campos-integracao type="compra"
                                dense
                                :form-data="cadastrosAdicionais"/>
        </v-col>
      </v-row>
      <!--      produtos-->
      <v-row>
        <v-col cols="12">
          <span class="headline font-weight-bold title">Produtos</span>
        </v-col>
      </v-row>
      <v-row dense class="mb-3">
        <v-expansion-panels class="rounded-card" multiple focusable
                            v-model="expandedPanels"
                            v-if="produtos && produtos.length">
          <v-expansion-panel
            active-class="rounded-card"
            v-for="(produto, index) in produtos"
            :key="produto.id"
          >
            <v-expansion-panel-header>
              <px-finalizacao-produto-panel-header ref="productHeader"
                                                   :produto="produto"
                                                   :campos-obrigatorios="camposObrigatorios"
                                                   :moeda="moeda"/>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <px-finalizacao-produto-panel-content ref="productContent"
                                                    :erp-campos-integracao="erpCamposIntegracao"
                                                    :campos-obrigatorios="camposObrigatorios"
                                                    :produto="produto"
                                                    @invalid="onProductContextInvalid(index)"/>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-row>
      <div class="div-actions d-flex justify-end">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <v-btn color="primary"
                     class="rounded-tag-small elevation-0 normal-btn-text"
                     @click.stop="finalizar">
                <span v-if="isAprovacaoEnabled">
                  Solicitar confirmação
                </span>
                <span v-else>
                  Enviar ordem de compra
                </span>
              </v-btn>
            </span>
          </template>
          <span v-if="isAprovacaoEnabled">
            Solicitar confirmação das informaçoes para o fornecedor
          </span>
          <span v-else>
            Enviar ordem de compra para fornecedor
          </span>
        </v-tooltip>
      </div>
    </div>
  </v-form>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { flatten, some } from 'lodash';
import { FormaPagamento, TipoFrete } from 'px-business-components';
import { required, requiredIf } from 'vuelidate/lib/validators';
import { GET_ITENS } from '@/store/carrinho/actions.type';
import validationMixin from '@/mixins/validation-mixin';
import EntidadeService from '@/api/entidade-service';
import GestaoFornecedorService from '@/modules/gestao-fornecedor/gestao-fornecedor-service';
import moment from 'moment';
import { formatToCode } from '@/utils/moment-date-utils';
import FinalizacaoProdutoPanelHeader
  from '@/modules/carrinho/finalizacao/finalizacao-produtos/FinalizacaoProdutoPanelHeader.vue';
import FinalizacaoProdutoPanelContent
  from '@/modules/carrinho/finalizacao/finalizacao-produtos/FinalizacaoProdutoPanelContent.vue';
import {
  computeFrom as computeFromProduto,
  validateCamposErp as validateCamposErpProduto,
} from '@/modules/carrinho/finalizacao/item-campos-erp/validate-campos-erp';
import moedaMixin from '@/mixins/moeda-mixin';
import EntidadeCamposCompraService
  from '@/modules/erp-campos-obrigatorios/entidade-campos-compra-service';
import {
  isFieldRequiredEntity,
} from '@/modules/erp-campos-obrigatorios/erp-campos-obrigatorios-utils';
import CarrinhoService from '../carrinho-service';
import {
  getObservacaoFrete,
  getPrazoEntrega,
  getTipoFrete,
  getValorFrete,
  solicitaFormaPagamento,
} from './finalizacao-utils';
import {
  computeFrom,
  initialStateCamposErp,
  validateCamposErp,
} from './campos-erp/validate-campos-erp';
import FinalizacaoCadastroMensagensPersonalizadas
  from './FinalizacaoCadastroMensagensPersonalizadas.vue';
import tipoNotaEntradaService from '../../tipo-nota-entrada/tipo-nota-entrada-service';

export default {
  name: 'Finalizacao',
  mixins: [validationMixin, moedaMixin],
  data() {
    return {
      hasDataNecessidadeEntregaSolicitacao: false,
      cadastrosAdicionais: null,
      camposObrigatorios: null,
      entidade: {},
      hasFileLoading: false,
      expandedPanels: [],
      arquivos: [],
      idsProdutos: [],
      loading: false,
      carrinhos: [],
      produtos: [],
      tiposFretes: TipoFrete.values,
      formasPagamentos: FormaPagamento.values,
      solicitaFormaPagamento: null,
      finalizacao: {
        comprador: null,
        compradorEntrega: null,
        transportadora: null,
        observacao: null,
        mensagem: null,
        dataFaturamento: formatToCode(moment()),
        dataPrevisaoEntrega: null,
        formaPagamento: null,
        arquivos: [],
        tipoNotaEntrada: null,
        centroCusto: null,
        projeto: null,
        prazoPagamento: null,
        modoPagamento: null,
        mensagensPersonalizadas: [],
        frete: {
          tipoFrete: null,
          observacaoFrete: null,
          prazoEntrega: null,
          totalTransporte: 0,
          ofertaFreteGratis: false,
          valorMinimoFreteGratis: 0,
        },
        dataNecessidadeEntregaSolicitacao: null,
      },
      produtosCarrinhosId: this.$route.params.id,
      breadcrumbs: [
        {
          text: 'Carrinho',
          to: '/carrinho',
        },
        {
          text: 'Finalização',
        },
      ],
    };
  },
  computed: {
    ...mapGetters('user', ['erpCamposIntegracao']),
    moeda() {
      // Se chegamos até, assumimos que todos os produtos tem a mesma moeda
      return this.carrinhos[0].respostaCotacao.moeda;
    },
    isAVista() {
      return this.finalizacao.formaPagamento !== FormaPagamento.keys.A_PRAZO.key;
    },
    isAprovacaoEnabled() {
      return !this.entidade.pularAprovacaoFornecedor;
    },
    isCif() {
      return this.finalizacao.frete.tipoFrete === TipoFrete.keys.CIF.key;
    },
    smModoPagamento() {
      return this.hasDataNecessidadeEntregaSolicitacao ? '3' : '6';
    },
    isModoPagamentoRequired() {
      return isFieldRequiredEntity('modoPagamento', this.camposObrigatorios);
    },
    isCentroCustoRequired() {
      return isFieldRequiredEntity('centroCusto', this.camposObrigatorios);
    },
    isTipoNotaEntradaRequired() {
      return isFieldRequiredEntity('tipoNotaEntrada', this.camposObrigatorios);
    },
    isTransportadoraRequired() {
      return isFieldRequiredEntity('transportadora', this.camposObrigatorios);
    },
  },
  methods: {
    ...mapActions('carrinho', {
      getItens: GET_ITENS,
    }),
    onRemoveMessage(item) {
      this.finalizacao.mensagensPersonalizadas = this.finalizacao
        .mensagensPersonalizadas.filter(mensagem => mensagem.id !== item.id);
    },
    onChangeTipoFrete() {
      const [carrinho] = this.carrinhos;
      this.setTransportador(carrinho);
    },
    getEntidade() {
      return EntidadeService.getEntidade()
        .then(({ data }) => {
          this.entidade = data;
        });
    },
    getDetalhesFornecedor(id) {
      return GestaoFornecedorService.getByFornecedor(id)
        .then(({ data }) => {
          this.cadastrosAdicionais = initialStateCamposErp(this.erpCamposIntegracao);
          this.cadastrosAdicionais = computeFrom(this.erpCamposIntegracao,
            data.cadastrosAdicionais);
        });
    },
    getTipoNotaEntrada() {
      return tipoNotaEntradaService.getNotaEntradaDefault()
        .then((response) => {
          this.finalizacao.tipoNotaEntrada = response.data || null;
        });
    },
    init() {
      this.idsProdutos = this.produtosCarrinhosId.split(',')
        .map(Number);
      this.loading = true;
      CarrinhoService.getItensCarrinho({
        query: `idProduto in (${this.produtosCarrinhosId})`,
      })
        .then((response) => {
          this.carrinhos = this.filterProdutosSelecionados(response.data);
          this.checkSolicitaFormaPagamento();
          this.checkSolicitaFrete();
          this.createProdutos();

          const [carrinho] = this.carrinhos;
          this.setCompradores(carrinho);
          this.setCentroCusto(carrinho);
          this.setProjeto(carrinho);
          this.setTransportador(carrinho);
          this.setDataPrevisaoEntrega(carrinho);
          this.setDataNecessidadeEntregaSolicitacao(carrinho);
          return Promise.all([
            this.getEntidade(),
            this.getDetalhesFornecedor(carrinho.respostaCotacao.fornecedor.id),
            this.getTipoNotaEntrada(),
          ]);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    filterProdutosSelecionados(carrinhos) {
      return carrinhos.map(carrinho => ({
        ...carrinho,
        produtos: carrinho.produtos.filter(produto => this.idsProdutos.includes(produto.id)),
      }));
    },
    setCompradores(carrinho) {
      const { cotacao } = carrinho.respostaCotacao;
      this.finalizacao.comprador = cotacao.comprador;
      this.finalizacao.compradorEntrega = cotacao.compradorEntrega;
    },
    setDataPrevisaoEntrega(carrinho) {
      this.finalizacao.dataPrevisaoEntrega = this.getPrevisaoEntrega(carrinho);
    },
    getPrevisaoEntrega(carrinho) {
      const { cotacao } = carrinho.respostaCotacao;
      return carrinho.dataNecessidadeEntregaSolicitacao || cotacao.prazoEntrega;
    },
    setDataNecessidadeEntregaSolicitacao(carrinho) {
      this.finalizacao.dataNecessidadeEntregaSolicitacao = carrinho
        .dataNecessidadeEntregaSolicitacao;
      this.hasDataNecessidadeEntregaSolicitacao = !!carrinho.dataNecessidadeEntregaSolicitacao;
    },
    onFileLoad(loading) {
      this.hasFileLoading = loading;
    },
    buildQuantidades() {
      // apenas por segurança, mas já devemos ter somente os produtos selecionados
      const produtos = this.produtos
        .filter(produto => this.idsProdutos.includes(produto.id));
      return produtos.map(p => ({
        carrinhoCotacaoProduto: {
          id: p.id,
        },
        quantidade: p.quantidade,
        tipoNotaEntrada: p.tipoNotaEntrada || null,
        centroCusto: p.centroCusto || null,
        projeto: p.projeto || null,
        especificacaoProduto: p.especificacaoProduto,
        observacaoExterna: p.observacaoExterna,
        cadastrosAdicionais: this.buildCadastrosAdicionaisProdutoPayload(p.cadastrosAdicionais),
      }));
    },
    setProjeto(carrinho) {
      const { cotacao } = carrinho.respostaCotacao;
      this.finalizacao.projeto = cotacao.projeto;
    },
    setProjetoProdutos() {
      this.produtos = this.produtos.map(produto => ({
        ...produto,
        projeto: produto.projeto ? produto.projeto : this.finalizacao.projeto,
      }));
    },
    setCentroCusto(carrinho) {
      const { cotacao } = carrinho.respostaCotacao;
      this.finalizacao.centroCusto = cotacao.centroCusto;
    },
    setCentroCustoProdutos() {
      this.produtos = this.produtos.map(produto => ({
        ...produto,
        centroCusto: produto.centroCusto ? produto.centroCusto : this.finalizacao.centroCusto,
      }));
    },
    setNotaEntradaProdutos() {
      this.produtos = this.produtos.map(produto => ({
        ...produto,
        tipoNotaEntrada: this.finalizacao.tipoNotaEntrada || null,
      }));
    },
    setTransportador(carrinho) {
      const { fornecedor } = carrinho.respostaCotacao;
      const { cotacao } = carrinho.respostaCotacao;
      if (this.finalizacao.transportadora === null && this.isCif) {
        this.finalizacao.transportadora = this.getTransportadora(fornecedor, cotacao);
      }
    },
    getTransportadora(fornecedor, cotacao) {
      const detalheFornecedor = cotacao.detalhesFornecedores
        .find(detalhe => detalhe.fornecedor.id === fornecedor.id);
      if (detalheFornecedor) {
        return {
          ...fornecedor,
          codigoInterno: detalheFornecedor.codigoInterno,
        };
      }
      return {
        ...fornecedor,
      };
    },
    buildCadastrosAdicionaisProdutoPayload(cadastrosAdicionaisProduto) {
      if (!cadastrosAdicionaisProduto) {
        return [];
      }
      return [
        cadastrosAdicionaisProduto.armazem,
        cadastrosAdicionaisProduto.contaContabil,
        cadastrosAdicionaisProduto.contaFinanceira,
      ].map((cadastro) => {
        if (cadastro) {
          return {
            id: cadastro.id,
          };
        }
        return null;
      }).filter(cadastro => cadastro);
    },
    buildCadastrosAdicionaisPayload() {
      if (!this.cadastrosAdicionais) {
        return [];
      }
      return [
        this.cadastrosAdicionais.natureza,
        this.cadastrosAdicionais.condicaoPagamento,
        this.cadastrosAdicionais.tipoCobranca,
      ].map((cadastro) => {
        if (cadastro) {
          return {
            id: cadastro.id,
          };
        }
        return null;
      }).filter(cadastro => cadastro);
    },
    buildFinalizacao() {
      const finalizacao = { ...this.finalizacao };
      finalizacao.carrinhos = this.carrinhos;
      finalizacao.quantidades = this.buildQuantidades();
      finalizacao.arquivos = this.arquivos;
      finalizacao.cadastrosAdicionais = this.buildCadastrosAdicionaisPayload();
      return finalizacao;
    },
    mapProdutos(produto) {
      const {
        cotacaoProduto,
        precoUnitario,
        arquivos,
      } = produto.respostaCotacaoProduto;
      return {
        id: produto.id,
        produto: cotacaoProduto.produto,
        precoUnitario,
        especificacaoProduto: cotacaoProduto.especificacaoProduto,
        observacaoExterna: cotacaoProduto.observacaoExterna,
        quantidade: cotacaoProduto.quantidade,
        tipoNotaEntrada: this.finalizacao.tipoNotaEntrada || null,
        centroCusto: cotacaoProduto.centroCusto
          ? cotacaoProduto.centroCusto : this.finalizacao.centroCusto,
        projeto: cotacaoProduto.projeto ? cotacaoProduto.projeto : this.finalizacao.projeto,
        unidadeMedida: cotacaoProduto.unidadeMedida,
        cadastrosAdicionais: this.mapCadastrosAdicionaisProdutos(cotacaoProduto),
        arquivosFornecedor: arquivos,
        arquivosComprador: cotacaoProduto.arquivos,
      };
    },
    mapCadastrosAdicionaisProdutos(cotacaoProduto) {
      if (this.erpCamposIntegracao) {
        return computeFromProduto(this.erpCamposIntegracao,
          cotacaoProduto.produto.cadastrosAdicionais);
      }
      return null;
    },
    createProdutos() {
      const produtos = flatten(this.carrinhos.map(c => c.produtos));
      this.produtos = produtos.map(this.mapProdutos);
    },
    checkSolicitaFormaPagamento() {
      this.solicitaFormaPagamento = solicitaFormaPagamento(this.carrinhos);
      this.finalizacao.formaPagamento = this.solicitaFormaPagamento.formaPagamento;
      this.finalizacao.prazoPagamento = this.solicitaFormaPagamento.prazoPagamento;
    },
    checkSolicitaFrete() {
      this.finalizacao.frete.tipoFrete = getTipoFrete(this.carrinhos);
      this.finalizacao.frete.observacaoFrete = getObservacaoFrete(this.carrinhos);
      this.finalizacao.frete.totalTransporte = getValorFrete(this.carrinhos);
      this.finalizacao.frete.prazoEntrega = getPrazoEntrega(this.carrinhos);
    },
    isInvalid() {
      const areProductHeadersInvalid = this.$refs.productHeader
        .some(header => header.validate());

      const areProductContentsInvalid = this.$refs.productContent && this.$refs.productContent
        .some(content => content.validate());

      if (this.validateForm() || areProductHeadersInvalid || areProductContentsInvalid) {
        return true;
      }
      const quantidades = this.produtos.map(p => p.quantidade);
      return some(quantidades, q => !q);
    },
    validateFile() {
      if (this.hasFileLoading) {
        this.$toast('Aguarde até o término do envio dos arquivos', { color: 'error' });
        return true;
      }
      return false;
    },
    validateCadastrosAdicionais() {
      const [isValid, message] = validateCamposErp(this.erpCamposIntegracao,
        this.cadastrosAdicionais);
      if (!isValid) {
        this.$toast(message, { color: 'error' });
      }
      const produtosValidos = this.produtos.map((respostaProduto) => {
        const [isValidProduto, messageProduto] = validateCamposErpProduto(this.erpCamposIntegracao,
          respostaProduto.cadastrosAdicionais);

        if (!isValidProduto) {
          this.$toast(messageProduto, { color: 'error' });
        }
        return isValidProduto;
      }).every(isValidProduto => isValidProduto);
      return isValid && produtosValidos;
    },
    finalizar() {
      if (this.isInvalid() || this.validateFile() || !this.validateCadastrosAdicionais()) {
        return;
      }
      this.loading = true;
      const finalizacaoCarrinho = this.buildFinalizacao();
      CarrinhoService.finalizar(finalizacaoCarrinho)
        .then(({ data }) => {
          this.getItens();
          this.$toast('Solicitação de confirmação enviada com sucesso', { color: 'success' });

          if (data.tipo === 'APROVACAO') {
            this.$router.push({
              name: 'main.aprovacao.detalhes',
              params: {
                id: data.id,
              },
            });
            return;
          }

          this.$router.push({
            name: 'main.compras.detalhes',
            params: { id: data.id },
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onClickBack() {
      this.$router.push({ name: 'main.carrinho' });
    },
    onChangeFormaPagamento() {
      this.finalizacao.prazoPagamento = null;
    },
    onProductContextInvalid(index) {
      this.expandedPanels.push(index);
    },
    getCamposObrigatorios() {
      EntidadeCamposCompraService.getCamposObrigatorios(this.erpCamposIntegracao)
        .then((data) => {
          this.camposObrigatorios = data;
        });
    },
  },
  created() {
    this.init();
    this.getCamposObrigatorios();
  },
  validations: {
    finalizacao: {
      formaPagamento: {
        required,
      },
      frete: {
        tipoFrete: {
          required,
        },
      },
      prazoPagamento: {
        // eslint-disable-next-line func-names
        required: requiredIf(function () {
          return !this.isAVista;
        }),
      },
      modoPagamento: {
        required: requiredIf(function () {
          return this.isModoPagamentoRequired;
        }),
      },
      centroCusto: {
        required: requiredIf(function () {
          return this.isCentroCustoRequired;
        }),
      },
      tipoNotaEntrada: {
        required: requiredIf(function () {
          return this.isTipoNotaEntradaRequired;
        }),
      },
      transportadora: {
        required: requiredIf(function () {
          return this.isTransportadoraRequired;
        }),
      },
      comprador: {
        required,
      },
      compradorEntrega: {
        required,
      },
      dataPrevisaoEntrega: {
        required,
      },
      dataFaturamento: {
        required,
      },
    },
  },
  components: {
    pxFinalizacaoProdutoPanelHeader: FinalizacaoProdutoPanelHeader,
    pxFinalizacaoProdutoPanelContent: FinalizacaoProdutoPanelContent,
    pxFinalizacaoCadastroMensagensPersonalizadas: FinalizacaoCadastroMensagensPersonalizadas,
  },
  watch: {
    'finalizacao.tipoNotaEntrada': {
      handler() {
        this.setNotaEntradaProdutos();
      },
    },
    'finalizacao.centroCusto': {
      handler() {
        this.setCentroCustoProdutos();
      },
    },
    'finalizacao.projeto': {
      handler() {
        this.setProjetoProdutos();
      },
    },
  },
};
</script>

<style scoped>
.div-actions {
  margin-top: 20px;
}
</style>
